/**
 * Keeps the user's working state across a session-expiry re-authentication.
 *
 * The state lives in the HashRouter fragment (`#/graph?countries=...&risks=...`),
 * and fragments are routinely dropped or mangled by auth redirect chains, so the
 * URL cannot be relied on to survive the round-trip. Instead, right before we
 * navigate to the auth page we snapshot the current URL, and once the app boots
 * again we send the user back to it.
 *
 * The snapshot lives in `sessionStorage`, not `localStorage`, because it must be
 * scoped to the one tab that was actually bounced to the auth page. The auth
 * redirect is a same-tab top-level navigation, so the value survives the round
 * trip; meanwhile a *different* tab opening the app root - a deliberate fresh
 * visit, session perfectly valid - cannot see it and is never hijacked.
 */
import {
    hasRestorableViewState,
    isAllowedRestoreUrl,
    isInternal,
    isLocalDNSMapping,
} from './configService';
import { VIEW } from '../interfaces';

const SNAPSHOT_KEY = '_preAuthUrl';
// Backstop only. Tab scoping already means the snapshot dies with the tab, and
// restoring is limited to a landing URL with no view state of its own, so this
// exists purely to stop genuinely ancient state resurfacing in a tab that was
// left sitting on the auth page. Generous, because the scenario this feature
// exists for is a tab left idle long enough for the session to expire - the
// user may only come back to it hours or days later.
// TODO: Verify this value with product
const SNAPSHOT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// The 401 handler can fire for several in-flight requests at once; the first
// capture is the accurate one.
let snapshotTaken = false;

type Snapshot = { href: string; savedAt: number };

// sessionStorage is unavailable in the web worker and can throw in Safari
// private mode, so every access is best-effort: failing to restore is a minor
// degradation, throwing during app boot is not.
function readRaw(): string | null {
    try {
        return window.sessionStorage.getItem(SNAPSHOT_KEY);
    } catch {
        return null;
    }
}

function writeRaw(value: string): void {
    try {
        window.sessionStorage.setItem(SNAPSHOT_KEY, value);
    } catch {
        // ignored
    }
}

export function clearPreAuthUrl(): void {
    try {
        window.sessionStorage.removeItem(SNAPSHOT_KEY);
    } catch {
        // ignored
    }
}

function isRestorableContext(href: string): boolean {
    return (
        !isInternal() &&
        !isLocalDNSMapping() &&
        !href.includes(VIEW.EXPORT_CHART)
    );
}

function parseSnapshot(raw: string, now: number): string | null {
    let snapshot: Snapshot;
    try {
        snapshot = JSON.parse(raw);
    } catch {
        return null;
    }
    const isFresh =
        Number.isFinite(snapshot?.savedAt) &&
        now - snapshot.savedAt < SNAPSHOT_TTL_MS;
    if (!isFresh || !isAllowedRestoreUrl(snapshot.href)) {
        return null;
    }
    return snapshot.href;
}

function decideRestore(
    href: string,
    raw: string | null,
    now: number
): { restoreUrl: string | null; shouldClear: boolean } {
    // Only ever restore onto a bare landing URL. If the user arrived carrying
    // their own route (bookmark, shared deep link, manual navigation), that
    // intent wins: drop the snapshot instead of redirecting them away from it.
    if (hasRestorableViewState(href)) {
        return { restoreUrl: null, shouldClear: true };
    }
    if (!raw) {
        return { restoreUrl: null, shouldClear: false };
    }
    return { restoreUrl: parseSnapshot(raw, now), shouldClear: true };
}

function isSameDocumentUrl(
    target: string,
    current: { origin: string; pathname: string; search: string }
): boolean {
    // Always parseable: the snapshot is validated by `isAllowedRestoreUrl`.
    const url = new URL(target);
    return (
        url.origin === current.origin &&
        url.pathname === current.pathname &&
        url.search === current.search
    );
}

export function savePreAuthUrl(href: string = window.location.href): void {
    if (snapshotTaken || !isRestorableContext(href)) {
        return;
    }
    if (!hasRestorableViewState(href) || !isAllowedRestoreUrl(href)) {
        return;
    }
    snapshotTaken = true;
    writeRaw(JSON.stringify({ href, savedAt: Date.now() }));
}

/**
 * Returns the URL to send the user back to after re-authentication, or null when
 * there is nothing to restore (normal app boot).
 */
export function getPostAuthRestoreUrl(
    href: string = window.location.href
): string | null {
    const { restoreUrl, shouldClear } = decideRestore(href, readRaw(), Date.now());
    if (shouldClear) {
        clearPreAuthUrl();
    }
    return restoreUrl;
}

/**
 * Navigates to the restore target. A document load always follows, so the caller
 * should render nothing.
 *
 * The snapshot usually differs from the landing URL only in the hash fragment,
 * and navigating to such a URL is a *same-document* navigation - the browser
 * updates the address bar but never reloads. That is not enough for the app:
 * the stores hydrate from the URL only on a real load or a POP navigation, so
 * the restored route would sit in the address bar while the view stayed on the
 * default. Force an explicit reload in that case, which is exactly what a
 * manual refresh does.
 *
 * This cannot loop: the snapshot is cleared before we get here, and the URL we
 * reload into carries view state, which `getPostAuthRestoreUrl` refuses to
 * restore onto.
 */
export function applyRestoreUrl(target: string): void {
    const isSameDocument = isSameDocumentUrl(target, window.location);

    // `replace` keeps the auth landing URL out of history, so Back does not
    // bounce the user through the redirect again.
    window.location.replace(target);

    if (isSameDocument) {
        window.location.reload();
    }
}
